import range from "lodash/range";
import { useRouter } from "next/router";
import { useContext, useEffect, useMemo } from "react";
import useSWR from "swr";
import shallow from "zustand/shallow";
import { BaseContext } from "~context";
import { useReservationStore } from "~features/seated-reservation/store";
import {
  CreateOrderInput,
  OrderType,
  GetReleaseByIdQuery,
  LineItemType,
  GetReleaseBySlugQuery,
} from "~graphql/sdk";

import * as actions from "~features/seated-reservation/store/actions/index";
import { useAccount } from "~hooks/useAccount";
import { useReCaptchaSDK } from "~hooks/useReCaptcha";
import { showToast } from "~lib/helpers";
import { useSeatedMultibuy } from "../../../hooks/multibuy/seated";
import { getEvent, getTickets, getTicketsByIds } from "../graphql/queries";
import { seatsToLineItems } from "../helpers/seatsToLineItems";
import { useReferralCode } from "~hooks/useReferralCode";
import { useCreateOrder } from "~hooks/reservation/useCreateOrder";

export const useSeatedReservation = ({
  type,
  typeId,
  release,
}: {
  type: "event" | "membership" | "release";
  typeId: string;
  release:
    | GetReleaseByIdQuery["release"]
    | GetReleaseBySlugQuery["getReleaseBySlug"];
}) => {
  const router = useRouter();
  const { organization } = useContext(BaseContext);
  const { isAdmin, isPOS } = useAccount();
  const { recaptchaSdkGenerator } = useReCaptchaSDK();
  const { referralIdOrCode } = useReferralCode();
  const { createOrderAndWaitForCompletion } = useCreateOrder();

  const [seats, selectedAddons, holdToken] = useReservationStore(
    (state) => [state.seats, state.addons, state.chart?.holdToken],
    shallow
  );

  const { error: eventError, data: event } = useSWR(
    type === "event" ? ["event", organization?.id, typeId] : null,
    getEvent,
    { revalidateIfStale: false }
  );

  const isChangingSeats = !!router?.query?.ownedTickets;

  const { data: ownedTicketsData, error: ownedTicketsError } = useSWR(
    organization?.id && router?.query?.ownedTickets
      ? ["tickets", organization?.id, typeId]
      : null,
    getTickets
  );

  const { data: ownedTicketData, error: ownedTicketError } = useSWR(
    organization?.id && router?.query?.ownedTickets
      ? [
          "ownedTickets",
          organization?.id,
          Array.isArray(router?.query?.ownedTickets)
            ? router?.query?.ownedTickets.join(",")
            : router?.query?.ownedTickets,
        ]
      : null,
    getTicketsByIds
  );

  const { activePromotions } = useSeatedMultibuy(
    event.multiBuyPromotions || release?.event?.multiBuyPromotions,
    seats
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const changeTickets = useMemo(() => {
    let tix = [];

    if (type === "event") {
      tix =
        ownedTicketsData?.myEventTickets?.length > 0
          ? ownedTicketsData?.myEventTickets
          : ownedTicketData?.getTicketsByIds;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Array.isArray(tix)
      ? tix?.filter(({ id }) =>
          Array.isArray(router?.query?.ownedTickets)
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              router?.query?.ownedTickets?.includes(id)
            : // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              router?.query?.ownedTickets.split(",").includes(id)
        )
      : [];
  }, [router?.query?.ownedTickets, ownedTicketsData, ownedTicketData]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const priceComp = isChangingSeats
    ? changeTickets?.reduce(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        (acc, { lineItem: { price, quantity } }) => (acc += price * quantity),
        0
      )
    : undefined;

  useEffect(() => {
    if (changeTickets?.length) {
      void actions.updateOptions({
        options: {
          amount: changeTickets.length,
        },
      });
    }
  }, [changeTickets]);

  useEffect(() => {
    if (router?.query?.posId) {
      if (localStorage.getItem("posId")) {
        localStorage.removeItem("posId");
        localStorage.setItem("posId", router?.query?.posId as string);
      } else {
        localStorage.setItem("posId", router?.query?.posId as string);
      }
    }
  }, [router?.query?.posId]);

  useEffect(() => {
    if (release && !release?.isActive && !isAdmin) {
      showToast("This is release is not active yet", "error");
      void router.replace("/");
    }
  }, [release]);

  const minSeatAmountOption = release ? release.minPurchaseQuantity : 1;
  let maxSeatAmountOption = release ? release.maxPurchaseQuantity : 10;

  if (isAdmin || isPOS) {
    maxSeatAmountOption = 100;
  }

  const seatAmountOptions = range(
    Math.max(minSeatAmountOption - 1, 0),
    Math.min(maxSeatAmountOption, 100)
  ).map((i) => ({
    value: i + 1,
    label: `${i === 0 ? `${i + 1} person` : `${i + 1} people`}`,
  }));

  const handleOrderCreation = async (): Promise<boolean> => {
    if (!seats?.length && !holdToken) {
      showToast("No seat selection found", "error");
      return false;
    }

    let orderType = Object.values(OrderType).includes(
      router.query?.type as OrderType
    )
      ? (router.query?.type as OrderType)
      : undefined;

    if (changeTickets?.length && !router?.query?.type) {
      orderType = OrderType.ChangeSeats;
    }

    const releasePasswordId =
      sessionStorage.getItem("releasePasswordId") || undefined;

    const seatLineItems = seatsToLineItems(
      seats,
      type,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      changeTickets?.map((changeTicket) => changeTicket.id) as string[]
    );

    const addonLineItems = release?.releaseEventAddons
      .map(({ eventAddon }) => {
        if (selectedAddons?.[eventAddon.id]) {
          return {
            type: LineItemType.Addon,
            addonId: eventAddon.id,
            quantity: selectedAddons?.[eventAddon.id],
          };
        }
      })
      .filter((a) => !!a);

    const input: CreateOrderInput = {
      lineItems: [...seatLineItems, ...addonLineItems],
      orderType,
      holdToken,
      ...(type === "event" && {
        releaseId: release?.id,
      }),
      ...(releasePasswordId && { releasePasswordId }),
      ...(activePromotions?.[0]?.promotion?.id && {
        multiBuyId: activePromotions?.[0]?.promotion?.id,
      }),
      ...referralIdOrCode,
    };

    return await createOrderAndWaitForCompletion(input);
  };

  return {
    changeTickets,
    error: eventError || ownedTicketsError || ownedTicketError,
    event,
    handleOrderCreation,
    isLoading:
      !event &&
      !eventError &&
      (!isChangingSeats || (ownedTicketsData && !ownedTicketsError)),
    release,
    seats,
    priceComp,
    seatAmountOptions,
  };
};
